import { useMemo, type VoidFunctionComponent } from 'react';

import Locator from '../../../../shared/Locator';
import { useParams } from '../../../contexts/RouterContext';
import { useCurrentRevision } from '../../../hooks/api/revisions/useCurrentRevision';
import { isAuthed, isProjectInCurrentOrg } from '../../../utils';
import Subnav from '../../generic/Subnav';
import { useProject } from '../contexts/ProjectContext';
import { useRevisionScanIdQueryParam } from '../hooks/useRevisionScanIdQueryParam';
import InventorySubnavItem from '../inventory/InventorySubnavItem';
import type { InventoryScope } from '../inventory/types';

import AuthedItems from './ProjectSubnavAuthedItems';
import IssuesItem from './ProjectSubnavItemIssues';
import useActiveKey from './useActiveKey';

type Props = {
  /**
   * The organization which the project belongs to. This is used to hide
   * certain subnav items on public projects if the user has "view any project"
   * permissions, but belongs to another org. They get to see the "public" view
   * of the project, but shouldn't be allowed to see settings (for example).
   *
   * @example 17
   */
  projectOrgId: number;
  /**
   * This is the URL without the branch/revision part, which is needed for the Activity/Settings routes.
   * @example `/projects/custom%2B17%2Fcool-project`
   */
  baseProjectURL: string;
  /**
   * This is the full URL with the branch/revision part.
   * @example `/projects/custom%2B17%2Fcool-project/refs/branch/main/rev00001`
   */
  baseRevisionURL: string;
  /**
   * The project locator, without the revision ID.
   * @example `custom+17/cool-project`
   */
  projectLocator: string;
  /**
   * The revision ID
   * @example `rev00001`
   */
  revisionId: string | null;
};

/**
 * ProjectSubnav is the Subnav when viewing a project.
 * For SubnavItems which display a count, we have broken them
 * into their own components which can be responsible for fetching their own data.
 */
const ProjectSubnav: VoidFunctionComponent<Props> = ({
  projectOrgId,
  baseProjectURL,
  baseRevisionURL,
  projectLocator,
  revisionId,
}) => {
  const { isLoading, data: revision } = useCurrentRevision();

  const activeKey = useActiveKey(baseProjectURL, baseRevisionURL);
  const isCurrentOrgProject = isProjectInCurrentOrg(projectOrgId);

  // eslint-disable-next-line @typescript-eslint/naming-convention -- this is the name of the param
  const { revision_id, reference } = useParams<{ revision_id: string; reference: string }>();
  const { revisionScanId: revisionScanIdFromQueryParam } = useRevisionScanIdQueryParam();

  const project = useProject();
  const lastAnalyzedRevision = new Locator(project.data?.last_analyzed_revision ?? '');
  const isDefaultBranch = reference === project.data?.default_branch;
  const isLatestRevision = revision_id === lastAnalyzedRevision.revision.value;
  const isOverviewAvailable = isDefaultBranch && isLatestRevision;
  const fallbackProjectRevisionLocator = useMemo(() => {
    if (project.data?.latest?.locator) {
      return project.data.latest.locator;
    }

    if (project.data?.latest?.loc?.revision) {
      return `${projectLocator}$${project.data.latest.loc.revision}`;
    }

    return project.data?.last_analyzed_revision ?? null;
  }, [
    project.data?.latest?.locator,
    project.data?.latest?.loc?.revision,
    project.data?.last_analyzed_revision,
    projectLocator,
  ]);

  //  Prevent re-rendering the InventorySubnavItem unless the projectLocator changes
  const inventoryScope = useMemo<InventoryScope>(() => ({
    scope: 'project',
    revision: projectLocator,
  }), [projectLocator]);

  return (
    <Subnav activeKey={activeKey}>
      <Subnav.Item to={baseRevisionURL} navKey="summary">
        Summary
      </Subnav.Item>
      {isCurrentOrgProject && (
        <IssuesItem
          baseRevisionURL={baseRevisionURL}
          projectLocator={projectLocator}
          //  Prefer to use the query param, which is set in the RevisionContainer if there isn't one set,
          // or when the user selects a different revision scan.
          revisionScanId={revisionScanIdFromQueryParam ?? revision?.latestRevisionScanId ?? undefined}
          revisionId={revisionId}
          isOverviewAvailable={isOverviewAvailable}
        />
      )}
      <InventorySubnavItem
        baseUrl={baseRevisionURL}
        scope={inventoryScope}
        isLoading={isLoading}
        fallbackProjectRevisionLocator={fallbackProjectRevisionLocator}
      />
      <Subnav.ItemWithCount
        to={`${baseRevisionURL}/browse/licenses`}
        navKey="browse/licenses"
        count={revision?.license_count ?? 0}
        isLoading={!revisionId || isLoading}
      >
        Licenses
      </Subnav.ItemWithCount>
      {isAuthed() ? (
        <AuthedItems
          isCurrentOrgProject={isCurrentOrgProject}
          baseRevisionURL={baseRevisionURL}
          baseProjectURL={baseProjectURL}
        />
      ) : (
        <Subnav.Item to={`${baseProjectURL}/see-more`} navKey="see-more">
          See More
        </Subnav.Item>
      )}
    </Subnav>
  );
};

export default ProjectSubnav;
